/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { FiAlertTriangle } from 'react-icons/fi'

import { conflictingShipments, formatShipmentTiming } from './shipmentTiming'
import type { ShipmentRecord } from './types'
import { SHIPMENT_STATUSES } from './types'

const statusLabel = (status: ShipmentRecord['status']): string =>
  SHIPMENT_STATUSES.find((s) => s.value === status)?.label ?? status

const route = (shipment: ShipmentRecord): string =>
  [shipment.originName, shipment.destinationName].filter(Boolean).join(' → ') || 'route not set'

export interface TrackerCommitmentsProps {
  /** Shown as the subject of each line, e.g. "Acme SN-123: Idle". */
  trackerLabel: string
  shipments: ShipmentRecord[]
  isLoading: boolean
  error?: string
  /** Proposed window, for the clash warning. */
  proposedStart: Date | null
  proposedEnd: Date | null
  /** Excluded from clash detection — a shipment never conflicts with itself. */
  ignoreShipmentId?: number
}

/**
 * What a tracker is already committed to, shown once one is picked.
 *
 * A device planned into another shipment is not an error — it may be a
 * deliberate hand-off, and shipments get planned far in advance — so this
 * reports rather than blocks, and the clash warning is advisory.
 */
export const TrackerCommitments: React.FC<TrackerCommitmentsProps> = ({
  trackerLabel,
  shipments,
  isLoading,
  error,
  proposedStart,
  proposedEnd,
  ignoreShipmentId,
}) => {
  if (isLoading) {
    return (
      <p className="text-sm text-base-content/60" data-testid="tracker-commitments-loading">
        <span className="loading loading-spinner loading-xs mr-1 align-middle" />
        Checking {trackerLabel}…
      </p>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-warning" data-testid="tracker-commitments-error">
        {error}
      </p>
    )
  }

  const clashes = conflictingShipments(shipments, proposedStart, proposedEnd, ignoreShipmentId)
  const clashIds = new Set(clashes.map((s) => s.id))

  if (shipments.length === 0) {
    return (
      <p className="text-sm text-base-content/70" data-testid="tracker-commitments">
        <span className="font-medium">{trackerLabel}:</span> Idle
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1" data-testid="tracker-commitments">
      {shipments.map((shipment) => (
        <p
          key={shipment.id}
          className={`text-sm ${clashIds.has(shipment.id) ? 'text-warning' : 'text-base-content/70'}`}
          data-testid={`tracker-commitment-${shipment.id}`}
        >
          <span className="font-medium">{trackerLabel}:</span> Assigned to shipment {shipment.shipmentName} (
          {shipment.id}), status: {statusLabel(shipment.status)}, {route(shipment)}, {formatShipmentTiming(shipment)}
        </p>
      ))}

      {clashes.length > 0 && (
        <p className="flex items-start gap-1 text-sm text-warning" data-testid="tracker-commitment-clash">
          <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Heads up — {trackerLabel} looks busy over the times you entered
            {clashes.length === 1 ? '' : ` (${String(clashes.length)} overlapping shipments)`}. You can still save this;
            check the dates if that was not intended.
          </span>
        </p>
      )}
    </div>
  )
}
